/**
 * Export des états (point hebdomadaire) en Excel professionnel
 * Avec en-tête, titre, logo, tableau et pied de page
 */
import ExcelJS from "exceljs";

const APP_NAME = "Road Alert AGEROUTE";

export interface EtatRowExcel {
  datecreation?: string;
  libellecommune?: string;
  itineraire?: string;
  surface?: number;
  libellestatut?: string;
  chefEquipe?: string;
}

function formatDateFr(dateStr: string): string {
  if (!dateStr) return "-";
  try {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
}

/** Extraire la partie base64 d'une data URL pour ExcelJS */
function getBase64FromDataUrl(dataUrl: string): string | null {
  if (!dataUrl || !dataUrl.startsWith("data:")) return null;
  const base64 = dataUrl.split(",")[1];
  return base64 || null;
}

export interface ExportEtatsExcelOptions {
  data: EtatRowExcel[];
  dateDebut: string;
  dateFin: string;
  filename?: string;
  /** Data URL du logo (même format que pour le PDF) pour l'en-tête */
  logoDataUrl?: string | null;
}

const HEADERS = ["Date création", "Commune", "Itinéraire", "Surface (m²)", "Statut", "Chef équipe"];
const HEADER_ROW = 8; // Ligne 8 = en-têtes du tableau (après en-tête, titre, période)
const LOGO_WIDTH = 180;
const LOGO_HEIGHT = 48;

export async function exportEtatsExcel({
  data,
  dateDebut,
  dateFin,
  filename = "rapport-etats.xlsx",
  logoDataUrl,
}: ExportEtatsExcelOptions): Promise<void> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Rapport des états", { views: [{ showGridLines: true }] });

  // Largeurs de colonnes
  ws.columns = [
    { width: 15 },
    { width: 20 },
    { width: 25 },
    { width: 15 },
    { width: 15 },
    { width: 30 },
  ];

  let rowIndex = 1;

  // --- Ligne 1 : logo + titre ---
  ws.getRow(rowIndex).height = logoDataUrl ? 36 : 22;
  if (logoDataUrl) {
    const base64 = getBase64FromDataUrl(logoDataUrl);
    if (base64) {
      const imageId = wb.addImage({
        base64,
        extension: "png",
      });
      ws.addImage(imageId, {
        tl: { col: 0.1, row: 0.15 },
        ext: { width: LOGO_WIDTH, height: LOGO_HEIGHT },
      });
    }
  }
  const titleCell = ws.getCell(rowIndex, 4); // Colonne D
  titleCell.value = APP_NAME;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { vertical: "middle" };
  rowIndex += 2;

  // --- Titre ---
  const titleRow = ws.getRow(rowIndex);
  titleRow.getCell(1).value = "Rapport des états - Point hebdomadaire";
  titleRow.getCell(1).font = { bold: true, size: 14 };
  rowIndex += 2;

  // --- Période ---
  ws.getRow(rowIndex).getCell(1).value = `Période : du ${formatDateFr(dateDebut)} au ${formatDateFr(dateFin)}`;
  rowIndex += 1;
  ws.getRow(rowIndex).getCell(1).value = `Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  ws.getRow(rowIndex).getCell(1).font = { color: { argb: "FF666666" } };
  rowIndex += 2;

  // --- En-têtes du tableau ---
  const headerRow = ws.getRow(HEADER_ROW);
  HEADERS.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF428BCA" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });
  headerRow.height = 22;

  // --- Données ---
  data.forEach((row, i) => {
    const r = ws.getRow(HEADER_ROW + 1 + i);
    const fill = i % 2 === 0 ? "FFF5F5F5" : "FFFFFFFF";
    [
      formatDateFr(row.datecreation ?? ""),
      row.libellecommune ?? "-",
      row.itineraire ?? "-",
      row.surface != null ? row.surface : "-",
      row.libellestatut ?? "-",
      row.chefEquipe ?? "-",
    ].forEach((val, colIndex) => {
      const cell = r.getCell(colIndex + 1);
      cell.value = val;
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
      cell.alignment = { vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "FFCCCCCC" } },
        left: { style: "thin", color: { argb: "FFCCCCCC" } },
        bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
        right: { style: "thin", color: { argb: "FFCCCCCC" } },
      };
    });
  });

  // --- Pied de page ---
  const footerStart = HEADER_ROW + data.length + 3;
  ws.getRow(footerStart).getCell(1).value = `${APP_NAME} - Rapport des états`;
  ws.getRow(footerStart).getCell(1).font = { bold: true };
  ws.getRow(footerStart + 1).getCell(1).value = `Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))}`;
  ws.getRow(footerStart + 1).getCell(1).font = { color: { argb: "FF666666" } };

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
